import type { Game } from '../../game/Game';
import { registerOverlay, registerPanel } from '../../ui/app/App';
import { openPanel } from '../../ui/app/store';
import { registerShipContent } from './content';
import { activeSea } from './runtime';
import { SailPanel } from './SailPanel';
import { SeaHud } from './SeaHud';
import { sailGame, sailingSystem, sailingZoneSetup } from './system';
import './sailing.css';

/**
 * Sailing (FEATURES D16): on the Gleaming Sea the player's actor becomes the
 * ship — click-to-steer helm with momentum, ship abilities, hull durability,
 * storms and fog banks, salvage, towing — plus the `sail` harbour panel
 * (repairs, crew, refits, setting sail) and the helm HUD.
 */

let installedOn: Game | null = null;

export function install(game: Game): void {
  if (installedOn === game) return;
  installedOn = game;
  sailGame.current = game;
  registerShipContent();
  registerPanel('sail', SailPanel);
  registerOverlay(SeaHud);
  game.zoneSetups.push((g, z) => sailingZoneSetup(g, z));
  game.systems.push(sailingSystem);
  // Wrecks at sea are 'chest' interactables (minimap icon, G within reach): salvage them instead of opening.
  const prev = game.interactHandlers.get('chest');
  game.interactHandlers.set('chest', (g, id) => salvageByHand(g, id) || (prev?.(g, id) ?? false));
  game.hud.addMenuButton('Ship', '', () => openPanel('sail'));
}

function salvageByHand(game: Game, id: string): boolean {
  const rt = activeSea();
  if (!rt || rt.z !== game.zone || !rt.wrecks.has(id)) return false;
  rt.salvage(id, 'interact');
  return true;
}

export { SAILORS, PORTS, WRECK_SITES, SHIP_MODEL, shipLevelInfo, upgradeCost } from './data';
export { shipOf, shipStats, statsOf, repairShip, upgradeShip, hireSailor, dismissSailor, towShip, lastPort } from './ship';
export { activeSea, setupSailingZone, type SailHost, type SeaRuntime } from './runtime';
export { HAZARDS, sampleHazards } from './hazards';
